import { gql } from "@apollo/client";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import React, { useContext, useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import styled from "styled-components";
import Alert from "../../components/Alert";
import HeadingOne from "../../components/HeadingOne";
import StyledButton from "../../components/StyledButton";
import ThanksScreen from "../../components/ThanksScreen";
import colors from "../../constants/colors";
import constants from "../../constants/constants";
import { GetUserContext } from "../../localStorage/Store";
import styl from "../../styl";
import Error404 from "../error/Error404";
import AboProduct, { getVariationLabel } from "./AboProduct";

const GetAboProducts = gql`
  query getAboProducts {
    __typename
    products(
      first: 20
      where: {
        status: "publish"
        category: "${constants.aboCategorySlug}"
        orderby: { field: DATE, order: ASC }
      }
    ) {
      nodes {
        ... on VariableProduct {
          id
          productId
          name
          description
          e2_shortDescription {
            shortdescription
          }
          e2_askUsAQuestion {
            askusaquestion
          }
          e3_origin {
            herkunft
          }
          image {
            sourceUrl
            altText
          }
          galleryImages {
            nodes {
              sourceUrl
              altText
            }
          }
          variations {
            nodes {
              variationId
              name
              price
              attributes {
                nodes {
                  name
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

const GetExtCart = gql`
  query getExtCart {
    __typename
    extCart(subordererKey: "") {
      addProductsUntil
      lists {
        name
      }
    }
  }
`;

const CheckAGBQuery = gql`
  query checkAgb {
    checkAgb {
      agb
      dsgvo
    }
  }
`;

const GetagbversionQuery = gql`
  query getAgbVersion {
    __typename
    generalOptions {
      agb {
        agbversion
      }
      dsgvo {
        dsgvoversion
      }
    }
  }
`;

const UpdateCustomerMutation = gql`
  mutation updateCustomer($id: ID, $agb: String, $dsgvo: String) {
    __typename
    updateCustomer(
      input: {
        clientMutationId: "UpdateCustomer"
        id: $id
        agb: $agb
        dsgvo: $dsgvo
      }
    ) {
      customer {
        agb
        dsgvo
      }
    }
  }
`;

const SwitchToIndividualOrderMutation = gql`
  mutation switchToIndividualOrder {
    __typename
    switchToIndividualOrder(input: { clientMutationId: "switch" }) {
      cart {
        total
      }
    }
  }
`;

const EmptyExtCartMutation = gql`
  mutation emptyExtCart {
    __typename
    emptyExtCart(input: { clientMutationId: "emptyCart", subordererKey: "" }) {
      success
    }
  }
`;

const AddToExtCartMutation = gql`
  mutation addToExtCart($cartItems: [ExtCartItemInput]!) {
    __typename
    addToExtCart(
      input: {
        clientMutationId: "AddToCart"
        cartItems: $cartItems
        subordererKey: ""
      }
    ) {
      cart {
        total
      }
    }
  }
`;

const ExtCheckoutMutation = gql`
  mutation extCheckout($shippingAddress: ExtShippingAddressInput) {
    __typename
    # Abos werden nie mit Kühlbox versendet
    # shippingAddress gilt nur für diese Bestellung, das Benutzerprofil bleibt unverändert
    extCheckout(
      input: {
        clientMutationId: "ExtCheckout"
        coolboxdisabled: true
        shippingAddress: $shippingAddress
      }
    ) {
      order {
        total
        orderNumber
        orderId
      }
    }
  }
`;

export default function AboPage() {
  const [user] = useContext(GetUserContext);
  const { loading, error, data } = useQuery(GetAboProducts);
  const [getExtCart] = useLazyQuery(GetExtCart, {
    fetchPolicy: "network-only",
  });
  const [checkAgb] = useLazyQuery(CheckAGBQuery, {
    fetchPolicy: "network-only",
  });
  const { data: agbVersionData } = useQuery(GetagbversionQuery);
  const [updateCustomer] = useMutation(UpdateCustomerMutation);
  const [switchToIndividualOrder] = useMutation(
    SwitchToIndividualOrderMutation,
  );
  const [emptyExtCart] = useMutation(EmptyExtCartMutation);
  const [addToExtCart] = useMutation(AddToExtCartMutation);
  const [extCheckout] = useMutation(ExtCheckoutMutation);

  // pending = { product, variation, shippingAddress, hasCart, hasGroupOrder, needsAgb }
  const [pending, setPending] = useState(null);
  const [ordering, setOrdering] = useState(false);
  const [order, setOrder] = useState();

  // Nach Login / Registrierung wird die zuvor gewählte Laufzeit wieder ausgewählt
  const initialVariationId = parseInt(
    new URLSearchParams(window.location.search).get("variation"),
  );

  useEffect(() => {
    document.title = "Echtes Käsehandwerk";
    // Abo-Seite soll nicht in Suchmaschinen erscheinen
    const robots = document.querySelector('meta[name="robots"]');
    const previous = robots?.getAttribute("content");
    robots?.setAttribute("content", "noindex, nofollow");
    return () => {
      if (previous) robots.setAttribute("content", previous);
    };
  }, []);

  // shippingAddress: abweichende Lieferadresse nur für diese Bestellung (null = Adresse aus dem Profil)
  const handleOrder = async (product, variation, shippingAddress) => {
    if (!user) {
      const redirect = `/abo?variation=${variation.variationId}`;
      window.location.href = "/login?redirect=" + encodeURIComponent(redirect);
      return;
    }

    setOrdering(true);
    try {
      const [{ data: cartData }, { data: agbData }] = await Promise.all([
        getExtCart(),
        checkAgb(),
      ]);
      const lists = cartData?.extCart?.lists || [];
      const nextPending = {
        product,
        variation,
        shippingAddress,
        hasCart: lists.length > 0,
        // Gruppenbestellung läuft, auch wenn noch niemand etwas hinzugefügt hat
        hasGroupOrder:
          !!cartData?.extCart?.addProductsUntil ||
          lists.some((list) => list.name !== "initiator"),
        needsAgb: !agbData?.checkAgb?.agb || !agbData?.checkAgb?.dsgvo,
      };

      if (
        nextPending.hasCart ||
        nextPending.hasGroupOrder ||
        nextPending.needsAgb
      ) {
        setPending(nextPending);
        setOrdering(false);
      } else {
        await submitOrder(nextPending);
      }
    } catch (error) {
      setOrdering(false);
      alert(error.toString());
    }
  };

  const submitOrder = async ({
    variation,
    shippingAddress,
    hasCart,
    hasGroupOrder,
    needsAgb,
  }) => {
    setOrdering(true);
    try {
      if (needsAgb) {
        await updateCustomer({
          variables: {
            id: user.id,
            agb: agbVersionData?.generalOptions?.agb?.agbversion,
            dsgvo: agbVersionData?.generalOptions?.dsgvo?.dsgvoversion,
          },
        });
      }
      // Abos werden immer einzeln bestellt: Gruppenbestellung auflösen und Warenkorb leeren
      if (hasGroupOrder) await switchToIndividualOrder();
      if (hasCart) await emptyExtCart();
      await addToExtCart({
        variables: {
          cartItems: [{ variationId: variation.variationId, amount: 1 }],
        },
      });
      const response = await extCheckout({
        variables: {
          shippingAddress: shippingAddress
            ? { ...shippingAddress, email: user.email }
            : null,
        },
      });
      setOrder(response.data.extCheckout.order);
      setPending(null);
      window.scrollTo(0, 0);
    } catch (error) {
      alert(error.toString());
    } finally {
      setOrdering(false);
    }
  };

  if (order) {
    return (
      <Main>
        <ContentContainer>
          <ThanksScreen order={order} location={"AboPage"} />
        </ContentContainer>
      </Main>
    );
  }

  if (error) return <Error404 />;

  const products =
    data?.products?.nodes?.filter((product) => product.productId) || [];

  return (
    <Main>
      <ContentContainer>
        <HeadingOne title="Echtes Käsehandwerk." />
        {loading ? (
          <ThreeDots
            height={400}
            width={200}
            color={colors.YellowBrigthButton}
          />
        ) : products.length === 0 ? (
          <Text>Derzeit sind keine Abos verfügbar.</Text>
        ) : (
          products.map((product) => (
            <AboProduct
              key={product.id}
              product={product}
              initialVariationId={initialVariationId}
              onOrder={handleOrder}
              loading={ordering}
              loggedIn={!!user}
            />
          ))
        )}
      </ContentContainer>

      {pending && (
        <Alert isVisible={true}>
          <Text>
            <strong>
              {pending.product.name} – {getVariationLabel(pending.variation)}
            </strong>{" "}
            (€ {pending.variation.price})
          </Text>
          {(pending.hasCart || pending.hasGroupOrder) && (
            <Text>
              {pending.hasGroupOrder
                ? "Es besteht eine laufende Gruppenbestellung. Diese wird abgebrochen und alle Produkte im Warenkorb werden entfernt, da Abos einzeln bestellt werden."
                : "In deinem Warenkorb befinden sich bereits Produkte. Diese werden entfernt, da Abos einzeln bestellt werden."}
            </Text>
          )}
          {pending.needsAgb && (
            <Text>
              Mit der Bestellung bestätige ich, dass ich die{" "}
              <Link target="_blank" href="/agb">
                AGBs
              </Link>{" "}
              und die{" "}
              <Link target="_blank" href="/datenschutz">
                Datenschutzerklärung
              </Link>{" "}
              gelesen habe und akzeptiere.
            </Text>
          )}
          <Buttons>
            <StyledButton
              scale={"ORDER"}
              title={"Zahlungspflichtig bestellen"}
              color={colors.YellowBrigthButton}
              fontSize={"17px"}
              loading={ordering}
              onClick={() => submitOrder(pending)}
            />
            <StyledButton
              scale={"ORDER"}
              title={"Abbrechen"}
              color={colors.YellowBrigthButton}
              fontSize={"17px"}
              onClick={() => setPending(null)}
            />
          </Buttons>
        </Alert>
      )}
    </Main>
  );
}

const Main = styled.div`
  padding-bottom: 25px;
  margin-left: 5vw;
`;

const ContentContainer = styled.div`
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  width: 70vw;
  margin-left: 20px;
  @media (max-width: 1980px) and (max-height: 1080px) {
    margin-left: 130px;
  }
  @media (max-width: 1500px) {
    width: 80%;
    margin: 62px;
  }
  @media (max-width: 990px) {
    width: 90%;
    margin: 5%;
  }
`;

const Text = styled.p`
  ${styl.lightDefaultFont};
  font-size: 14px;
  line-height: 20px;
  color: ${colors.SolidBlackText};
`;

const Link = styled.a`
  color: #b2982a;
  text-decoration: underline;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 15px;
`;
